// ControlsSummaryPage — comprehensive control summary with category breakdown,
// lifecycle state counts, effectiveness ratings, and overdue detection.

import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { reportingApi } from '@/lib/api/reporting'
import type { CategorySummary, ControlSummaryRow } from '@/lib/api/reporting'
import { queryKeys } from '@/lib/api/queryKeys'
import { ErrorState } from '@/components/shared/ErrorState'
import { TableSkeleton } from '@/components/shared/LoadingSkeleton'
import { Shield, AlertTriangle } from 'lucide-react'

export function ControlsSummaryPage() {
  const { slug } = useTenant()
  const api = useApi()
  const rptApi = reportingApi(api)
  const navigate = useNavigate()

  const { data, error, isLoading, refetch } = useQuery({
    queryKey: queryKeys.reporting.controlsSummary(slug),
    queryFn: () => rptApi.controlsSummary(),
  })

  if (isLoading) return <TableSkeleton rows={10} />

  if (error) {
    return (
      <div className="p-6">
        <ErrorState
          message="Failed to load controls summary"
          technicalDetail={error.message}
          onRetry={() => refetch()}
        />
      </div>
    )
  }

  if (!data) return null

  const overdueCount = data.controls.filter((c) => c.assessment_overdue).length

  return (
    <div className="space-y-6 overflow-y-auto p-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Controls Summary</h1>
        <p className="text-sm text-muted-foreground">
          Comprehensive view of all controls you have permission to see, with category breakdown
          and assessment status.
        </p>
      </div>

      {/* Top-level summary cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        <SummaryCard label="Total Controls" value={data.total_controls} />
        {data.by_state.map((s) => (
          <SummaryCard
            key={s.lifecycle_state}
            label={s.lifecycle_state.charAt(0).toUpperCase() + s.lifecycle_state.slice(1)}
            value={s.count}
          />
        ))}
        {overdueCount > 0 && (
          <SummaryCard label="Overdue Assessment" value={overdueCount} color="text-red-600" />
        )}
      </div>

      {/* Category breakdown */}
      {data.by_category.length > 0 && (
        <div>
          <h2 className="mb-3 text-lg font-semibold">By Category</h2>
          <div className="overflow-hidden rounded-md border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-3 py-2 text-left font-medium">Category</th>
                  <th className="px-3 py-2 text-center font-medium">Total</th>
                  <th className="px-3 py-2 text-center font-medium">Active</th>
                  <th className="px-3 py-2 text-center font-medium">Draft</th>
                  <th className="px-3 py-2 text-center font-medium">Effective</th>
                  <th className="px-3 py-2 text-center font-medium">Partial</th>
                  <th className="px-3 py-2 text-center font-medium">Ineffective</th>
                  <th className="px-3 py-2 text-center font-medium">Unassessed</th>
                  <th className="px-3 py-2 text-center font-medium">Overdue</th>
                </tr>
              </thead>
              <tbody>
                {data.by_category.map((cat) => (
                  <CategoryRow key={cat.category_id} category={cat} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Controls table */}
      {data.controls.length === 0 ? (
        <div className="flex h-48 items-center justify-center text-muted-foreground">
          <div className="space-y-2 text-center">
            <Shield className="mx-auto h-10 w-10 text-muted-foreground/40" />
            <p className="text-sm font-medium">No controls found</p>
            <p className="text-xs">No controls exist that you have permission to view.</p>
          </div>
        </div>
      ) : (
        <div>
          <h2 className="mb-3 text-lg font-semibold">All Controls</h2>
          <div className="overflow-hidden rounded-md border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-3 py-2 text-left font-medium">Control</th>
                  <th className="px-3 py-2 text-left font-medium">Category</th>
                  <th className="px-3 py-2 text-left font-medium">State</th>
                  <th className="px-3 py-2 text-left font-medium">Design</th>
                  <th className="px-3 py-2 text-left font-medium">Operating</th>
                  <th className="px-3 py-2 text-left font-medium">Last Assessed</th>
                  <th className="px-3 py-2 text-left font-medium">Owner</th>
                  <th className="px-3 py-2 text-center font-medium">Risks</th>
                  <th className="w-8 px-2 py-2" />
                </tr>
              </thead>
              <tbody>
                {data.controls.map((ctrl) => (
                  <ControlRow
                    key={ctrl.id}
                    control={ctrl}
                    onClick={() => navigate({ to: `/t/${slug}/controls/${ctrl.id}` })}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

// --- Sub-components ---

function SummaryCard({
  label,
  value,
  color,
}: {
  label: string
  value: number
  color?: string
}) {
  return (
    <div className="rounded-md border p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-2xl font-bold tabular-nums ${color ?? ''}`}>{value}</p>
    </div>
  )
}

function CategoryRow({ category }: { category: CategorySummary }) {
  return (
    <tr className="border-b last:border-0">
      <td className="px-3 py-2 font-medium">{category.category_name || '—'}</td>
      <td className="px-3 py-2 text-center tabular-nums">{category.total}</td>
      <td className="px-3 py-2 text-center tabular-nums">{category.active}</td>
      <td className="px-3 py-2 text-center tabular-nums text-muted-foreground">
        {category.draft}
      </td>
      <td className="px-3 py-2 text-center tabular-nums text-green-600">
        {category.effective_count}
      </td>
      <td className="px-3 py-2 text-center tabular-nums text-amber-600">
        {category.partially_effective_count}
      </td>
      <td className="px-3 py-2 text-center tabular-nums text-red-600">
        {category.ineffective_count}
      </td>
      <td className="px-3 py-2 text-center tabular-nums text-muted-foreground">
        {category.not_assessed_count}
      </td>
      <td className="px-3 py-2 text-center tabular-nums">
        {category.overdue_assessment_count > 0 ? (
          <span className="font-medium text-red-600">{category.overdue_assessment_count}</span>
        ) : (
          <span className="text-muted-foreground">0</span>
        )}
      </td>
    </tr>
  )
}

function ControlRow({ control, onClick }: { control: ControlSummaryRow; onClick: () => void }) {
  return (
    <tr className="cursor-pointer border-b last:border-0 hover:bg-muted/30" onClick={onClick}>
      <td className="max-w-[200px] truncate px-3 py-2 font-medium" title={control.name}>
        {control.name}
      </td>
      <td className="px-3 py-2 text-muted-foreground">{control.category_name || '—'}</td>
      <td className="px-3 py-2">
        <StateBadge state={control.lifecycle_state} />
      </td>
      <td className="px-3 py-2">
        <RatingBadge rating={control.latest_design_rating} />
      </td>
      <td className="px-3 py-2">
        <RatingBadge rating={control.latest_operating_rating} />
      </td>
      <td className="whitespace-nowrap px-3 py-2 tabular-nums">
        {control.latest_assessment_date
          ? new Date(control.latest_assessment_date).toLocaleDateString()
          : '—'}
      </td>
      <td className="max-w-[120px] truncate px-3 py-2 text-muted-foreground">
        {control.owner_display_name || '—'}
      </td>
      <td className="px-3 py-2 text-center tabular-nums">{control.linked_risk_count}</td>
      <td className="px-2 py-2 text-center">
        {control.assessment_overdue && (
          <AlertTriangle className="inline h-3.5 w-3.5 text-destructive" />
        )}
      </td>
    </tr>
  )
}

function StateBadge({ state }: { state: string }) {
  const styles: Record<string, string> = {
    draft: 'bg-gray-100 text-gray-700',
    active: 'bg-green-100 text-green-700',
    retired: 'bg-blue-100 text-blue-700',
  }
  return (
    <span
      className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${styles[state] ?? 'bg-gray-100 text-gray-700'}`}
    >
      {state}
    </span>
  )
}

function RatingBadge({ rating }: { rating: string }) {
  if (!rating) return <span className="text-xs text-muted-foreground">—</span>
  const styles: Record<string, string> = {
    effective: 'bg-green-100 text-green-700',
    partially_effective: 'bg-amber-100 text-amber-700',
    ineffective: 'bg-red-100 text-red-700',
    not_assessed: 'bg-gray-100 text-gray-700',
  }
  const label = rating
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
  return (
    <span
      className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${styles[rating] ?? 'bg-gray-100 text-gray-700'}`}
    >
      {label}
    </span>
  )
}
